import { useMemo } from 'react';

interface EntityWithDeleted {
  id: number | string;
  isDeleted?: boolean;
}

/**
 * Hook for building select options that include deleted items if currently selected.
 * When an item is deleted but still selected in the data, it should appear in the options
 * to prevent validation errors and maintain data integrity.
 *
 * @param selectedItem - The currently selected item (may be deleted)
 * @param activeItems - The list of active (non-deleted) items
 * @returns Combined list with deleted item prepended if necessary
 *
 * @example
 * ```tsx
 * const { data: departments } = useDepartments();
 * const departmentOptions = useOptionsWithDeleted(
 *   teamMemberData?.department,
 *   departments || []
 * );
 * ```
 */
export const useOptionsWithDeleted = <T extends EntityWithDeleted>(
  selectedItem: T | null | undefined,
  activeItems: T[]
): T[] => {
  return useMemo(() => {
    if (selectedItem?.isDeleted) {
      return [selectedItem, ...activeItems];
    }
    return activeItems;
  }, [selectedItem, activeItems]);
};
